// Hotspots on the board image, in a 2000 x 1412 coordinate system (the board art's aspect ratio).
import type { SpaceId, Resource } from "../../../../common/unfathomable";

export const kBoardW = 2000;
export const kBoardH = 1412;

type Pt = [number, number];

export interface SpaceGeom {
  /** Clickable outline. */
  poly: Pt[];
  /** Where character tokens are laid out (left to right). */
  people: Pt;
  /** Where Deep One / passenger markers are drawn. */
  monsters: Pt;
}

const G = (poly: Pt[], people: Pt, monsters: Pt): SpaceGeom => ({ poly, people, monsters });

export const kSpaceGeom: Record<SpaceId, SpaceGeom> = {
  // water
  W14: G([[0, 470], [330, 470], [580, 645], [300, 760], [30, 915], [0, 915]], [0, 0], [200, 650]),
  Wu3: G([[330, 470], [1040, 470], [1040, 640], [580, 645]], [0, 0], [820, 560]),
  Wu5: G([[1040, 470], [1700, 470], [1490, 640], [1040, 640]], [0, 0], [1280, 560]),
  Wu7: G([[1700, 470], [2000, 470], [2000, 915], [1950, 915], [1900, 760], [1700, 660], [1490, 640]], [0, 0], [1830, 600]),
  W58: G([[0, 915], [30, 915], [300, 1070], [580, 1190], [330, 1330], [0, 1330]], [0, 0], [200, 1180]),
  Wl4: G([[580, 1190], [1040, 1190], [1040, 1330], [330, 1330]], [0, 0], [820, 1265]),
  Wl6: G([[1040, 1190], [1490, 1190], [1700, 1330], [1040, 1330]], [0, 0], [1280, 1265]),
  Wl8: G([[1490, 1190], [1700, 1170], [1900, 1070], [1950, 915], [2000, 915], [2000, 1330], [1700, 1330]], [0, 0], [1830, 1230]),
  // deck
  D1: G([[30, 915], [300, 760], [580, 645], [580, 770], [420, 770], [370, 830], [370, 915]], [300, 815], [470, 720]),
  D2: G([[30, 915], [370, 915], [370, 1000], [420, 1065], [580, 1065], [580, 1190], [300, 1070]], [300, 1015], [470, 1115]),
  D3: G([[580, 645], [1040, 640], [1040, 770], [580, 770]], [740, 715], [930, 715]),
  D4: G([[580, 1065], [1040, 1065], [1040, 1190], [580, 1190]], [740, 1125], [930, 1125]),
  D5: G([[1040, 640], [1490, 640], [1490, 770], [1040, 770]], [1200, 715], [1380, 715]),
  D6: G([[1040, 1065], [1490, 1065], [1490, 1190], [1040, 1190]], [1200, 1125], [1380, 1125]),
  D7: G([[1490, 640], [1700, 660], [1900, 760], [1950, 915], [1760, 915], [1760, 830], [1700, 770], [1490, 770]], [1600, 710], [1830, 830]),
  D8: G([[1490, 1065], [1700, 1065], [1760, 1000], [1760, 915], [1950, 915], [1900, 1070], [1700, 1170], [1490, 1190]], [1600, 1125], [1830, 1000]),
  // interior
  bridge: G([[420, 770], [580, 770], [580, 1065], [420, 1065], [370, 1000], [370, 830]], [475, 860], [475, 960]),
  chapel: G([[580, 770], [1040, 770], [1040, 915], [995, 915], [995, 850], [810, 850], [810, 915], [580, 915]], [690, 805], [930, 805]),
  cabin: G([[580, 915], [810, 915], [810, 985], [995, 985], [995, 915], [1040, 915], [1040, 1065], [580, 1065]], [690, 1030], [930, 1030]),
  sickbay: G([[810, 850], [995, 850], [995, 985], [810, 985]], [902, 950], [902, 950]),
  cargo: G([[1040, 770], [1490, 770], [1490, 915], [1460, 915], [1460, 855], [1325, 855], [1325, 915], [1040, 915]], [1150, 800], [1395, 805]),
  galley: G([[1040, 915], [1325, 915], [1325, 980], [1460, 980], [1460, 915], [1490, 915], [1490, 1065], [1040, 1065]], [1150, 1035], [1395, 1025]),
  brig: G([[1325, 855], [1460, 855], [1460, 980], [1325, 980]], [1392, 945], [1392, 945]),
  boiler: G([[1490, 770], [1700, 770], [1760, 830], [1760, 1000], [1700, 1065], [1490, 1065]], [1590, 975], [1700, 900]),
};

export const kDeepAnchor: Pt = [400, 400];
export const kChaosAnchor: Pt = [305, 175];

/** Track box centres (index 0 = Start, 4 = Arrive / Cast). */
export const kTravelBoxes: Pt[] = [[848, 116], [928, 116], [1008, 116], [1086, 116], [1166, 116]];
export const kRitualBoxes: Pt[] = [[848, 232], [928, 232], [1008, 232], [1086, 232], [1166, 232]];

export const kDials: Record<Resource, { c: Pt; r: number }> = {
  fuel: { c: [1545, 148], r: 78 },
  food: { c: [1810, 148], r: 78 },
  sanity: { c: [1545, 390], r: 78 },
  souls: { c: [1810, 390], r: 78 },
};

/** Needle angle (degrees clockwise from 12 o'clock) for a dial value 0..10. */
export function dialAngle(v: number): number {
  return -120 + 24 * Math.max(0, Math.min(10, v));
}

export function polyPoints(poly: Pt[]): string {
  return poly.map(p => p.join(",")).join(" ");
}

export function polyCentroid(poly: Pt[]): Pt {
  const x = poly.reduce((a, p) => a + p[0], 0) / poly.length;
  const y = poly.reduce((a, p) => a + p[1], 0) / poly.length;
  return [x, y];
}
